import { Offcanvas, Row } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { FormProvider, useForm } from 'react-hook-form';
import { AddEntityDrawerProps } from './EntityListProps';
import DrawerWrapper, { DrawerFooter } from './DrawerWrapper';
import FormTextField from './formFields/FormTextField';
import FormTextAreaField from './formFields/FormTextAreaField';
import { validateDateOrder } from '../../utils/validateDateOrder';
import { BsMicrosoftTeams } from 'react-icons/bs';

import {
  eventTypes,
  IncidentCalendarDisplayEvent,
  useAddIncidentCalendarEvent,
} from './hooks/incidentCalendarHooks';

import { useGetRosterForIncident } from 'hooks/incident/useRoster';

import FormDateTimeField from './formFields/FormDateTimeField';
import FormSelectField from './formFields/FormSelectField';
import { StylishNewChip } from 'components/DesignSystems/New/StylishNewChip';
import CalendarEventAddAttendeeDialog from './CalendarEventAddAttendeeDialog';

const CalendarEventAddDrawer: React.FC<AddEntityDrawerProps> = ({ toggle }) => {
  const form = useForm<IncidentCalendarDisplayEvent>({
    mode: 'onChange',
  });
  const { handleSubmit, watch, setValue, getValues } = form;

  const color = watch('color');
  const startValue = watch('start');
  const endValue = watch('end');
  const isOnlineMeeting = watch('isOnlineMeeting');

  useEffect(() => {
    if (color) {
      const eventType = eventTypes?.find((type) => type.code === color);
      if (eventType) {
        setValue('purpose', eventType.agenda);
        setValue('title', eventType.title);
      }
    }
  }, [color, setValue]);

  useEffect(() => {
    if (startValue && endValue) {
      form.trigger(['start', 'end']);
    }
  }, [startValue, endValue, form]);

  const mutation = useAddIncidentCalendarEvent();

  const { data: incidentRoster } = useGetRosterForIncident();

  const [incidentMembersToAttend, setIncidentMembersToAttend] = useState([]);

  const [showAddAttendeeDialog, setShowAddAttendeeDialog] = useState(false);

  const submitEventAdd = async (formData: IncidentCalendarDisplayEvent) => {
    const aggregateData = {
      ...formData,
      internalAttendees: [...incidentMembersToAttend],
    };
    await mutation.mutateAsync(aggregateData);
    toggle();
  };

  const handleRecipientDeleteClick = (memberGuid: string) => {
    setIncidentMembersToAttend((prev) =>
      prev.filter((guid: string) => guid !== memberGuid)
    );
  };

  return (
    <>
      <DrawerWrapper toggle={toggle} title="Add Event">
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(submitEventAdd)}
            className="d-flex flex-column justify-content-between flex-grow-1"
          >
            <Offcanvas.Body>
              <Row>
                <FormSelectField
                  title="Event Type"
                  fieldName="color"
                  classes="border-color-change"
                  style={{
                    borderColor: color,
                  }}
                  options={eventTypes?.map((type) => {
                    return {
                      label: type.title,
                      value: type.code,
                    };
                  })}
                  variant="half"
                  rules={{
                    required: {
                      value: true,
                      message: 'Event type is required',
                    },
                  }}
                />
                <FormTextField
                  title="Title"
                  fieldName="title"
                  rules={{
                    required: {
                      value: true,
                      message: 'This field is required',
                    },
                    maxLength: {
                      value: 255,
                      message: 'This maximum length is 255 characters',
                    },
                  }}
                  variant="half"
                />
              </Row>
              <Row>
                <FormDateTimeField
                  title="Start Date & Time"
                  fieldName="start"
                  variant="full"
                  rules={{
                    required: {
                      value: true,
                      message: 'Start date and time is required',
                    },
                    validate: (value: string) => {
                      return validateDateOrder(value, endValue);
                    },
                  }}
                />
              </Row>
              <Row>
                <FormDateTimeField
                  title="End Date & Time"
                  fieldName="end"
                  variant="full"
                  rules={{
                    required: {
                      value: true,
                      message: 'End date and time is required',
                    },
                    validate: (value: string) => {
                      return validateDateOrder(startValue, value);
                    },
                  }}
                />
              </Row>
              <Row>
                <p className="mb-2">Incident Roster Attendees</p>
                {(!!incidentMembersToAttend &&
                  !!incidentMembersToAttend.length && (
                    <>
                      <div className="form-chip-group mb-4">
                        {incidentMembersToAttend
                          .filter((memberGuid: string) =>
                            incidentRoster?.find(
                              (person) => person.userId === memberGuid
                            )
                          )
                          .map((memberGuid: string) => {
                            const user = incidentRoster?.find(
                              (person) => person.userId === memberGuid
                            );
                            const name = user?.userName || user?.emailAddress;
                            return (
                              <>
                                <StylishNewChip
                                  key={`key-${memberGuid}`}
                                  label={name || ''}
                                  handleDelete={() => {
                                    handleRecipientDeleteClick(memberGuid);
                                  }}
                                />
                              </>
                            );
                          })}
                      </div>
                    </>
                  )) || (
                  <p className="text-muted">
                    No incident roster attendees selected
                  </p>
                )}
                <StylishNewButton
                  className="button--secondary"
                  onClick={() => {
                    // Logic to open a modal or another drawer to select incident members
                    setShowAddAttendeeDialog(true);
                  }}
                >
                  Add Incident Members
                </StylishNewButton>
              </Row>
              <Row>
                <FormTextAreaField
                  title="External Attendees"
                  fieldName="externalAttendees"
                  rules={{
                    maxLength: {
                      value: 255,
                      message: 'This maximum length is 255 characters',
                    },
                  }}
                  variant="full"
                />
              </Row>
              <Row>
                <FormTextAreaField
                  title="Agenda"
                  fieldName="purpose"
                  rules={{
                    required: {
                      value: true,
                      message: 'This field is required',
                    },
                    maxLength: {
                      value: 5000,
                      message: 'This maximum length is 5000 characters',
                    },
                  }}
                  variant="full"
                  defaultValue={
                    eventTypes?.find((type) => type.code === watch('color'))
                      ?.agenda || ''
                  }
                />
              </Row>
              <Row className="align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                  <BsMicrosoftTeams size={20} color="#6264A7" />
                  <span id="teams-meeting-label">Teams meeting</span>
                  <div className="form-switcher ms-2">
                    <input
                      type="checkbox"
                      id="teams-meeting-toggle-add"
                      aria-labelledby="teams-meeting-label"
                      checked={!!isOnlineMeeting}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setValue('isOnlineMeeting', checked, {
                          shouldDirty: true,
                        });
                        if (checked) {
                          setValue('location', 'Teams Meeting', {
                            shouldDirty: true,
                          });
                        } else if (getValues('location') === 'Teams Meeting') {
                          setValue('location', '', {
                            shouldDirty: true,
                          });
                        }
                      }}
                    />
                    <label htmlFor="teams-meeting-toggle-add"></label>
                  </div>
                </div>
              </Row>
              <FormTextField
                title="Location"
                fieldName="location"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 255,
                    message: 'This maximum length is 255 characters',
                  },
                }}
                variant="half"
              />
            </Offcanvas.Body>
            <DrawerFooter>
              <div className="button-group">
                <StylishNewButton
                  className="button--secondary button--reverse"
                  type="button"
                  onClick={toggle}
                >
                  Close
                </StylishNewButton>
                <StylishNewButton
                  className="button--primary"
                  type="submit"
                  disabled={mutation.isPending}
                >
                  Save
                </StylishNewButton>
              </div>
            </DrawerFooter>
          </form>
        </FormProvider>
      </DrawerWrapper>
      {showAddAttendeeDialog && (
        <CalendarEventAddAttendeeDialog
          onClose={() => setShowAddAttendeeDialog(false)}
          incidentRoster={incidentRoster || []}
          incidentMembersToAttend={incidentMembersToAttend}
          setIncidentMembersToAttend={setIncidentMembersToAttend}
        />
      )}
    </>
  );
};

export default CalendarEventAddDrawer;
